import { cache } from 'react';

import { fetchWithAwsWaf } from 'lib/security/awsWaf';
import config from 'utils/config';
import { drainResponseBody } from 'utils/drainResponseBody';
import { isBuildPhase, isDemoBackedBuild } from 'utils/isBuildPhase';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { parseRetryAfterMs } from 'utils/retryAfter';

// The shared demo backend occasionally responds slower than production's own API —
// give it more room before aborting instead of surfacing a spurious timeout locally.
const INTERACTIVE_TIMEOUT_MS = config.backendEnv === 'demo' ? 60_000 : 10_000;

/**
 * The per-request deadline during `next build`, which is not the interactive one.
 *
 * That 60s is a *local dev* allowance: a human waiting on one slow demo response would
 * rather wait than see a spurious timeout. A prerender is the opposite situation — it has
 * `staticPageGenerationTimeout` for the **whole page**, layouts and every serial fetch stage
 * included, and a demo-backed build inherited the 60s.
 *
 * Four attempts at 60s plus three capped waits is 285s, so a single unlucky request could
 * exceed the page budget on its own — with no page-level deadline to catch it, Next kills the
 * render, retries the page twice more, and exits the build. That is the `/matchups/nfl/...`
 * failure, and it is a distinct failure mode from a throttled request exhausting its retries:
 * the page never gets as far as a guard.
 *
 * This is the one term in `BUILD_RETRY_BUDGET_MS`'s arithmetic that the budget itself cannot
 * bound — a wall clock can stop the loop from *starting* another attempt, but not stop the
 * one already in flight — so it has to be bounded here or the budget is a guess.
 *
 * 20s rather than the 10s a production-backed build uses, because the reason for the demo
 * allowance does not disappear during a build — demo really is the slower backend, and the
 * point is to bound the deadline, not to start failing reads that would have answered.
 */
const BUILD_TIMEOUT_MS = config.backendEnv === 'demo' ? 20_000 : 10_000;

/**
 * Read at call time, not captured at module load.
 *
 * `isBuildPhase` is itself a load-time constant, so a captured copy would be correct — but
 * capturing it here evaluates an *imported* binding during this module's own initialization,
 * which is the module-scope deref this repo has been bitten by before. Concretely: a test that
 * mocks `utils/isBuildPhase` with a getter over a `let` gets a TDZ crash, because importing
 * anything from this file hoists its initializer above the test's own declarations. The
 * function defers that read to a point where every module involved is initialized, and costs
 * a branch per request.
 */
const requestTimeoutMs = (): number => (isBuildPhase ? BUILD_TIMEOUT_MS : INTERACTIVE_TIMEOUT_MS);

/**
 * Escalating backoff for a transient failure during `next build`, jittered below.
 *
 * Long, and deliberately so. The schedule this replaces was `[500, 1500, 5000]`, and the
 * thing to notice about it is that **a 429 is a fast response**: the limiter refuses in
 * milliseconds rather than spending the request deadline, so those three delays *are*
 * essentially the whole window — about 7s, 14s with jitter. A rate limiter's window is
 * typically a minute. Giving up after 7s of a 60s window is not really retrying; it is
 * sampling the same closed door three times in a row and then failing the deploy.
 *
 * That matters more than it used to, because riding the limiter out is now the *only* thing
 * standing between a 429 and a failed build: the guards downstream refuse a failure-derived
 * page unconditionally, so a request that runs out of retries takes the deploy with it.
 *
 * So the escalation runs out to the `MAX_BUILD_RETRY_DELAY_MS` ceiling and then stays there,
 * and `BUILD_RETRY_BUDGET_MS` — not the length of this array — is what actually decides when
 * to stop. The array is the shape of the wait; the budget is the size of it. Read them
 * together: extending this array does not lengthen the window, and shortening the budget does.
 *
 * One schedule for every build, demo-backed or not. An earlier cut gave demo a single
 * 500ms retry on the theory that nothing a preview produces serves users — true, but it is
 * the wrong axis. What decides how long retrying is worth it is the limiter's window, which
 * is a property of the backend being throttled, not of who reads the deployment, and the
 * budget below bounds both the same way.
 */
const BUILD_RETRY_DELAYS_MS = [500, 2_000, 6_000, 15_000, 15_000, 15_000, 15_000];
const RUNTIME_RETRY_DELAYS_MS = [250, 750];

/**
 * The runtime schedule for a 429 specifically, picked per attempt in the loop below.
 *
 * The general runtime schedule above is sized for the failures that *cost* something to
 * retry — a timeout has already spent the request deadline, a 5xx usually means the origin is
 * working on being unhealthy for a while. A 429 is neither: the limiter refuses in
 * milliseconds, so attempts are nearly free and the waits are the whole window. Two waits
 * totalling ~1–2s against a limiter window that is typically much longer is the same mistake
 * the build schedule's comment describes — sampling a closed door, not retrying — and it is
 * what killed the `/markets/render/...` ISR fills: "got HTTP 429 ... retrying in 285ms", once
 * more at ~1.3s, exhausted.
 *
 * Why not build-length patience? Because this layer cannot tell an ISR *revalidation* render
 * (stale page already serving, nobody waiting) from a user-facing SSR fill (somebody very
 * much waiting) — Next exposes no request context here, and reaching for `headers()` would
 * drag every caller into dynamic rendering. So the schedule stays conservative for both:
 * worst case fully jittered is 1s + 4s + 5s = 10s of waiting on top of three fast refusals,
 * bounded by the schedule itself the way the short one always was. That covers a short
 * limiter window outright and buys a real chance at a longer one, without holding a
 * user-facing render for anything like the build budget.
 *
 * Mixed failures bound themselves: the schedule is chosen per attempt from what that attempt
 * got, and `attemptsLeft` is checked against the chosen schedule — so a 503 arriving on
 * attempt three stops the loop (the general schedule is spent) even though a 429 would have
 * earned one more wait.
 */
const RUNTIME_RATE_LIMIT_RETRY_DELAYS_MS = [500, 2_000, 5_000];

/**
 * Wall-clock ceiling on one request's whole retry loop during `next build`.
 *
 * The real budget, and the reason the schedule above can afford to be long. Counting
 * *attempts* bounds nothing useful, because what each attempt costs depends on how it fails:
 * seven fast 429s cost almost nothing but the waits, while seven timeouts cost seven
 * deadlines. Counting time bounds the thing that actually has to fit —
 * `staticPageGenerationTimeout`, which is the whole page's budget, layouts and every serial
 * fetch stage included, with nothing shared between them.
 *
 * Checked against the *next* wait rather than the last one: a retry whose delay would cross
 * the deadline is not started at all, so the loop cannot overshoot by a sleep it has already
 * committed to. Worst case per request is therefore the budget plus one in-flight attempt —
 * 95s against demo, 85s against production — where the schedule this replaces could reach
 * 285s against demo with no ceiling of any kind.
 *
 * 75s is one limiter window plus room, and `staticPageGenerationTimeout` (300s, raised in
 * `next.config.js` alongside this) is what it has to fit inside: three fully throttled serial
 * stages is 285s, which fits, and a page deeper than that would have been over the old 180s
 * on the old schedule too.
 */
const BUILD_RETRY_BUDGET_MS = 75_000;

// Ceiling on a single wait, so honouring a large `Retry-After` can't stall a prerender on one
// upstream's say-so. It is a ceiling, not a budget — `BUILD_RETRY_BUDGET_MS` is the budget.
const MAX_BUILD_RETRY_DELAY_MS = 15_000;
const MAX_RUNTIME_RETRY_DELAY_MS = 2_000;
// The 429 schedule gets a matching ceiling: `retryDelayMs` already treats `Retry-After` as a
// floor on the jittered wait, so raising the cap is what lets a limiter's own "come back in
// 4s" actually be honoured at runtime instead of being clamped to 2s and refused again.
const MAX_RUNTIME_RATE_LIMIT_RETRY_DELAY_MS = 5_000;

export interface FetchServerSideBehavior {
    /** Defaults to build-only retries. An explicit false disables retries in every phase. */
    retryTransientErrors?: boolean;
    /**
     * Marks a request whose failure is a known, non-actionable condition — a
     * shadow read of an endpoint that is not deployed everywhere yet, say.
     *
     * `logError` reaches `console.error`, which Datadog alerts on, so an expected
     * failure logged that way pages someone on every cache refresh. Such requests
     * log at warning level instead and resolve to `null`, and they skip the
     * retry schedule entirely: a discardable read has nothing to salvage by
     * burning the backoff on it.
     */
    expectedFailure?: boolean;
    /** Use warning-level telemetry when a bounded best-effort request exhausts its retries. */
    failureLogLevel?: 'error' | 'warning';
    onAttempt?: (attempt: FetchServerSideAttempt) => void;
    /**
     * Opt out of sharing an identical in-flight request with another caller in the same
     * render (see `inFlightKey`). Defaults to sharing.
     *
     * Only needed by a caller that must observe the origin itself rather than an answer from
     * it — a health check, or a probe timing the round trip. A caller that merely wants fresh
     * data does not need this: the shared request is the same request, issued at the same
     * moment, with the same caching directives.
     */
    coalesce?: boolean;
}

export interface FetchServerSideAttempt {
    attempt: number;
    maxAttempts: number;
    outcome: 'response' | 'network_error';
    status?: number;
    willRetry: boolean;
}

const notifyAttempt = (behavior: FetchServerSideBehavior, attempt: FetchServerSideAttempt): void => {
    try {
        behavior.onAttempt?.(attempt);
    } catch {
        // Observability must not affect the request or its retry policy.
    }
};

const isRetryableFetchError = (error: unknown): boolean =>
    error instanceof TypeError ||
    (error instanceof Error && (error.name === 'NetworkError' || error.name === 'TimeoutError'));

/**
 * A URL safe to put in a log line: every query value replaced, nothing else touched.
 *
 * Some of this function's callers authenticate *in the query string* rather than in a header —
 * `fetchWcWrappedByLaunchToken` and `fetchWcWrappedByShareToken` pass signed tokens as `?t=` and
 * `?s=`, and each one authorizes reading somebody's recap. Logging the URL they failed on would
 * copy a live credential into Datadog, where it outlives the outage that produced it.
 *
 * Values rather than the whole query, because the parameter *names* are most of the diagnostic
 * value ("which endpoint, asked which way") and none of the risk. Redaction is unconditional: an
 * allowlist of "sensitive" names would have to be right about every caller, including the ones
 * added after this comment.
 *
 * Duplicate keys collapse into one entry, which is fine for a log line and worth knowing before
 * reading one. A URL that will not parse loses everything from the `?` on, since the alternative
 * is guessing at where its query starts.
 */
const redactUrlForLog = (url: string): string => {
    try {
        const parsed = new URL(url);
        // Snapshot the keys: mutating the params while iterating them is not sound.
        for (const key of [...parsed.searchParams.keys()]) {
            parsed.searchParams.set(key, 'REDACTED');
        }
        // Never sent to a server, but it can still reach a log.
        parsed.hash = '';
        return parsed.toString();
    } catch {
        return url.split('?')[0];
    }
};

/**
 * `TypeError: fetch failed` plus the part that actually says what happened.
 *
 * undici reports every transport failure under that one message and puts the detail on `cause`,
 * which neither `${error}` nor a default log line includes — so a socket-level failure logs as
 * "fetch failed" and nothing else, for every cause there is.
 */
const describeFetchRejection = (error: unknown): string => {
    const cause = error instanceof Error ? error.cause : undefined;
    if (!(cause instanceof Error)) return `${error}`;
    const code = (cause as { code?: string }).code;
    return `${error} (cause: ${cause.message}${code ? `, code ${code}` : ''})`;
};

/**
 * Name the URL on a rejection that leaves by the non-retrying path, without catching it.
 *
 * That path returns its promise rather than awaiting it, deliberately (see the comment above
 * `send()`), which also means the outer `catch` never sees the rejection and never logs the URL
 * that produced it. What reaches the platform log is then a bare `TypeError: fetch failed` whose
 * only surviving stack frame is `@vercel/otel`'s global-fetch wrapper — the same line for every
 * call site in the app, naming neither the upstream nor the route. One such line cost an
 * afternoon to trace back to this function at all.
 *
 * Attaching a handler that rethrows changes nothing a caller observes: the returned promise still
 * rejects with the original error, and callers written against that behaviour keep it. Warning
 * rather than error level because the rejection is on its way to a caller that may well handle
 * it, and `logError` pages someone.
 */
const logEscapingRejection = (url: string, responsePromise: Promise<Response>): Promise<Response> =>
    responsePromise.catch((error: unknown) => {
        logProviderFactory.logWarning(
            `Server-side fetch to ${redactUrlForLog(url)} rejected without retries: ${describeFetchRejection(error)}`,
        );
        throw error;
    });

/**
 * The statuses the retry schedule below treats as transient. Exported so a caller that
 * classifies the *final* response of an exhausted retry — which this function returns
 * as-is rather than swallowing — draws the transient/unexpected line the same way the
 * retry loop did, instead of keeping a second copy of the rule that can drift from it.
 *
 * The two transient 4xx statuses are here rather than lumped in with the rest of the
 * range: 408 says the upstream gave up waiting for a request it never finished reading,
 * and 425 that it refused to risk replaying early data. Both describe the *attempt*, not
 * the request, so both are worth repeating — and because `isFinalUpstreamError` is this
 * predicate inverted, leaving them out would also let a caller read a timeout as the
 * upstream's settled answer.
 */
export const isRetryableStatus = (status: number): boolean =>
    status === 408 || status === 425 || status === 429 || status >= 500;

/**
 * A non-OK upstream response that a caller chose to surface as a throw, carrying the status
 * that produced it.
 *
 * Plain `Error`s lose that status, which leaves a caller string-matching the message to tell
 * "the backend rate-limited us" apart from "this endpoint is not deployed here" — and the
 * second is worth knowing, because it is the one answer a second attempt cannot change.
 */
export class UpstreamResponseError extends Error {
    readonly status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = 'UpstreamResponseError';
        this.status = status;
    }
}

/**
 * True only for a failure the upstream reported as **final** — a status it will repeat
 * verbatim, such as a 404 for an endpoint this environment does not have.
 *
 * Deliberately not "everything that is not transient". A failure that arrived without a
 * status — a timeout, a cold handshake, an aborted connection — is *unknown* rather than
 * final, and a caller retrying on its own deadline is usually doing it for exactly those.
 * This predicate exists so such a caller can skip the one case where a second attempt can
 * only read the same answer back. `readNflPowerRankingsWithRetry` is that caller.
 */
export const isFinalUpstreamError = (error: unknown): boolean =>
    error instanceof UpstreamResponseError && error.status !== undefined && !isRetryableStatus(error.status);

/**
 * Wait for one retry attempt.
 *
 * Jittered because the backoff exists to survive a *self-inflicted* burst: `next build` fans
 * out across `experimental.cpus` workers, so a rate limit tends to reject several concurrent
 * prerenders in the same instant. A fixed schedule then marches all of them back in lockstep
 * and they collide again. Spreading each wait over [base, 2×base) decorrelates them — a
 * retry never fires sooner than the base schedule, and worst-case added wait roughly doubles
 * to ~14s for a request that would otherwise ship a degraded page.
 *
 * `Retry-After`, when the upstream sends one, is a directive rather than a hint: retrying
 * before it elapses just burns an attempt. Treat it as a floor on the jittered delay.
 */
function retryDelayMs(response: Response | undefined, base: number, maxDelayMs: number): number {
    const jittered = base + Math.random() * base;
    const retryAfter = response ? (parseRetryAfterMs(response) ?? 0) : 0;
    return Math.round(Math.min(Math.max(jittered, retryAfter), maxDelayMs));
}

/**
 * Sleep out one backoff, cut short if the caller's own deadline expires first.
 *
 * Checking `aborted` around the sleep is not enough on its own: the signal usually fires
 * *during* a wait, and a 15s ceiling wait is long enough that noticing only afterwards is
 * most of the waste. Waking early hands control back to the loop, whose
 * `callerDeadlineSpent` check then ends it — at the cost of one already-dead request, which
 * is deliberate: it produces the caller's real `TimeoutError` rather than a synthesized one.
 *
 * The listener is removed on every path. An `AbortSignal.timeout` stays referenced by its
 * own timer until it fires, so a listener left behind on a retry that did not need it would
 * accumulate across the schedule.
 */
function waitForRetry(delay: number, signal: AbortSignal | undefined): Promise<void> {
    return new Promise((resolve) => {
        if (!signal) {
            setTimeout(resolve, delay);
            return;
        }
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, delay);
        function onAbort() {
            clearTimeout(timer);
            resolve();
        }
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * One in-flight slot per request identity, scoped to the render that asked for it.
 *
 * `cache()` is React's render-scoped memo — the same mechanism Next's own `createDedupeFetch`
 * uses. Two calls with the same key inside one render get the same object; **outside** a
 * render it does not memoize at all, so every call gets a fresh slot and nothing is ever
 * shared. That is the correct degradation rather than a limitation: a route handler or a
 * script has no request scope to share within, and a slot that outlived one would be a
 * cross-request cache nobody asked for.
 */
const getInFlightSlot = cache((_key: string): InFlightSlot => ({ promise: null, joined: false }));

interface InFlightSlot {
    promise: Promise<InFlightResult> | null;
    /** Set by a joiner before it awaits, so the leader knows whether to reproduce the body. */
    joined: boolean;
}

/**
 * What the shared promise resolves to.
 *
 * Two shapes, because the overwhelmingly common case is a request nobody joins, and that case
 * must cost nothing: the leader hands back the original `Response`, untouched and unread, with
 * its streaming and its identity intact. Only a request that actually acquired a joiner pays
 * to be reproduced.
 */
type InFlightResult =
    | { shared: true; captured: CapturedResponse | null }
    | { shared: false; response: Response | null };

/**
 * A response taken apart so it can be handed to more than one caller.
 *
 * A `Response` body reads exactly once, so coalescing has to reproduce it. The body is read
 * to completion here and each caller gets a fresh `Response` over the same bytes.
 */
interface CapturedResponse {
    status: number;
    statusText: string;
    headers: Headers;
    body: ArrayBuffer | null;
}

const captureResponse = async (response: Response | null): Promise<CapturedResponse | null> => {
    if (!response) return null;
    return {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        // Reading to completion is what releases undici's chunk queue — the same disposal
        // `drainResponseBody` performs by cancelling. A null body (204, 304) must stay null:
        // the `Response` constructor rejects a body on those statuses.
        body: response.body ? await response.arrayBuffer() : null,
    };
};

const replayResponse = (captured: CapturedResponse | null): Response | null =>
    captured &&
    new Response(captured.body, {
        status: captured.status,
        statusText: captured.statusText,
        headers: captured.headers,
    });

// Tracing headers differ per call by design and must not split the identity, exactly as in
// Next's `generateCacheKey`.
const HEADERS_EXCLUDED_FROM_IDENTITY = new Set(['traceparent', 'tracestate']);

/**
 * The properties of a normalized `Request` that change the response, and so go into the key.
 *
 * Read off a `Request` rather than off the caller's init, because the platform has already
 * solved the normalization: an omitted `redirect` and an explicit `redirect: 'follow'` are the
 * same request, and reading the init directly makes them two different keys and two requests.
 * Casing and header canonicalization come free for the same reason.
 *
 * The first seven are exactly Next's own `generateCacheKey` list, so this stays auditable
 * against it. `cache` is the one addition, and it is deliberate — see `inFlightKey`.
 */
const KEYED_REQUEST_PROPERTIES = [
    'method',
    'mode',
    'redirect',
    'credentials',
    'referrer',
    'referrerPolicy',
    'integrity',
    'cache',
] as const satisfies readonly (keyof Request)[];

/**
 * Fields that provably do not change the response, and so neither key nor disqualify.
 *
 * `priority` is a scheduling hint, `window` must be null, and `duplex` only describes a
 * streaming request body — which a GET cannot have.
 */
const IGNORED_INIT_FIELDS = ['priority', 'window', 'duplex'] as const;

/**
 * Every init field `inFlightKey` claims to understand.
 *
 * Normalizing through a `Request` covers the *standard* fields, but it cannot cover the
 * non-standard ones: `new Request` silently drops anything it does not recognise, so an
 * extension that changes the response would vanish from the key rather than split it. Next
 * has the same hole and says so — "we currently don't consider non-standard (or future)
 * options. This might not be safe."
 *
 * Hence an allowlist over the init, checked before the `Request` is built. A field nobody
 * here has heard of disqualifies the call instead of being ignored, which costs that caller
 * nothing but its own request. `body` and `keepalive` fall out of it for free.
 */
const UNDERSTOOD_INIT_FIELDS = new Set<string>([
    ...KEYED_REQUEST_PROPERTIES,
    ...IGNORED_INIT_FIELDS,
    'headers',
    'next',
    'signal',
]);

/** Same rule one level down, since `next` is an object we read field by field. */
const UNDERSTOOD_NEXT_FIELDS = new Set<string>(['revalidate', 'tags']);

/**
 * The identity two calls must share to be answered by one request, or `null` for a call that
 * must keep its own.
 *
 * ## Why this is not simply Next's `generateCacheKey`
 *
 * The obvious objection to a hand-written key is that Next already has one, and reimplementing
 * it is how you get it subtly wrong. The standard half of this *is* theirs —
 * `KEYED_REQUEST_PROPERTIES` is their list, read off the same normalized `Request`, so the two
 * can be diffed. But theirs cannot be adopted wholesale, for two reasons that are documented
 * in their own source:
 *
 * 1. **It deliberately ignores `cache`** — "We don't include the `cache` field, because we end
 *    up using whatever caching resulted from the first request" — and it knows nothing of
 *    `next.revalidate`/`next.tags`, which is where this app expresses freshness. Under that
 *    rule the markets metadata read (300s) and page read (60s) collapse into one, and
 *    whichever ran first silently decides the other's staleness. That is the bug class the
 *    markets ISR fix exists for, so the caching directives have to split the key here.
 * 2. Their key is attached to a mechanism that **retains a cloned response per URL for the
 *    whole render**, which is the retention this codebase has a heap incident about (see the
 *    note on `fetchServerSide`).
 *
 * So the key is ours, and the parts Next omits are the parts we add.
 *
 * Anything on the init this function does *not* model disqualifies the call rather than being
 * ignored — see `UNDERSTOOD_INIT_FIELDS` for why that direction is the safe one.
 *
 * So is everything in `behavior` that changes what a caller observes. `retryTransientErrors`
 * changes how long the answer takes and how many attempts it costs. `failureLogLevel` changes
 * whether an exhausted failure reaches `logError` — which pages someone — and only the leader
 * ever runs that branch, so sharing across a disagreement would apply one caller's alerting
 * policy to the other's failure. The dangerous direction is the quiet one: an `error` caller
 * led by a best-effort `warning` caller loses an alert it was relying on.
 *
 * The rest of `behavior` is accounted for by a bail-out above rather than by the key, which
 * is the invariant to preserve when adding a field: either it cannot differ between sharers,
 * or it keeps them apart.
 */
function inFlightKey(url: string, options: RequestInit | undefined, behavior: FetchServerSideBehavior): string | null {
    // The browser has no render-scoped store to share within, and this module is bundled into
    // client components.
    if (typeof window !== 'undefined') return null;
    // Never coalesce something that may have side effects. A GET is safe to answer twice from
    // one request; a POST is not, and `fetchServerSide` does carry POSTs.
    const method = (options?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') return null;
    // A caller-supplied signal is Next's own opt-out rule and the right one: that caller owns
    // the request's lifetime, and its abort must not cancel a request another caller is
    // waiting on.
    if (options?.signal) return null;
    // A discardable read skips the retry schedule and is expected to fail; it has nothing to
    // share and nothing to save.
    if (behavior.expectedFailure) return null;
    // `onAttempt` reports per-attempt telemetry. A joiner issues no attempts, so its callback
    // could only ever report someone else's — leave such calls out rather than lie to them.
    if (behavior.onAttempt) return null;
    if (behavior.coalesce === false) return null;

    // Anything this key does not model means the caller asked for something it cannot
    // reason about, and the safe answer is its own request. `body` and `keepalive` fall out
    // here for free, as does any field a future runtime adds.
    const next = options?.next as Record<string, unknown> | undefined;
    if (Object.keys(options ?? {}).some((field) => !UNDERSTOOD_INIT_FIELDS.has(field))) return null;
    if (next && Object.keys(next).some((field) => !UNDERSTOOD_NEXT_FIELDS.has(field))) return null;

    try {
        // Built only to be measured, never sent — the request below is still issued from the
        // url and init, so this cannot change what goes on the wire. Safe to construct here
        // precisely because the non-GET bail above already ran: a GET carries no body for the
        // constructor to disturb.
        const request = new Request(url, options);
        // `Request` already lowercases and merges header names; sorting makes the order the
        // caller happened to use irrelevant too.
        const headers = Array.from(request.headers.entries())
            .filter(([name]) => !HEADERS_EXCLUDED_FROM_IDENTITY.has(name.toLowerCase()))
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
        return JSON.stringify([
            request.url,
            headers,
            ...KEYED_REQUEST_PROPERTIES.map((property) => request[property]),
            next?.revalidate ?? null,
            next?.tags ?? null,
            behavior.retryTransientErrors ?? null,
            // The *effective* level, not the raw field, so a caller that spells out the
            // default still shares with one that omits it — the same normalization the
            // `Request` performs for everything above.
            behavior.failureLogLevel ?? 'error',
        ]);
    } catch {
        // `new Request` rejects a malformed url, header or option, and this runs outside the
        // handler that turns a failed request into a logged `null`. An unshareable request is
        // a far better outcome than a throw from a new place, and the request itself will
        // fail the same way it always did once `fetch` normalizes the same arguments.
        return null;
    }
}

// undici's default connect timeout (10s) can trip before our own deadline ever gets a
// chance to, since it bounds the TCP connect phase separately from the AbortSignal below.
// Align it with ours when talking to the shared demo backend, so one deadline governs the
// request rather than two racing: interactively that raises it to `INTERACTIVE_TIMEOUT_MS`
// (60s), and during a build to `BUILD_TIMEOUT_MS` (20s — this branch only runs against demo).
// Both are ceilings on a connect that is failing, not budgets a healthy one spends, and the
// build figure is the one the 42s-per-request arithmetic on `BUILD_TIMEOUT_MS` is written
// against.
// This file is also bundled into client components (e.g. PerpsListClient), so the
// `undici` import must stay dynamic and Node-only — a static import drags in
// `node:net`, which breaks the browser bundle.
//
// Kick the import off at module load, but expose it as a promise so `fetchServerSide`
// can await it before issuing the first request. A fire-and-forget `.then()` races the
// first server-side fetch, which could still connect under undici's default 10s timeout.
const dispatcherReady: Promise<void> | null =
    typeof window === 'undefined' && config.backendEnv === 'demo'
        ? import('undici')
              .then(({ Agent, setGlobalDispatcher }) => {
                  setGlobalDispatcher(new Agent({ connect: { timeout: requestTimeoutMs() } }));
              })
              // A setup failure shouldn't block the fetch — fall back to undici's default
              // dispatcher rather than rejecting and failing every server-side request.
              .catch((e) => {
                  logProviderFactory.logError(`Failed to raise undici connect timeout for demo backend: ${e}`);
              })
        : null;

/**
 * Answer identical concurrent reads from one request.
 *
 * ## The gap this closes
 *
 * Next already deduplicates identical fetches within a render — except it doesn't, for
 * anything that goes through here. `createDedupeFetch` returns the raw fetch the moment an
 * init carries a `signal` ("we assume that someone else controls the lifetime of this
 * object ... It's effectively the opt-out mechanism"), and the request below always sets one
 * to bound the deadline. So every server fetch in this app has been opting out, at every call
 * site, silently.
 *
 * Deduplication is the *inner* layer of Next's stack (`patchedFetcher → dedupeFetch → raw
 * fetch`), so the Data Cache still works and the cost only lands on a miss. That is also
 * precisely when it hurts: a cold render fires its duplicates at the origin, together, and a
 * rate limiter sees double the load at the only moment it was going to be close. That is how
 * one `/markets/render/...` render sent `/v1/events/<ticker>` twice in the same millisecond,
 * collected two 429s, and failed.
 *
 * ## Why coalescing and not Next's memoization
 *
 * Next keeps a cloned response per URL for the whole render so a *later* call in the same
 * render can be answered from it. That retention is the part this codebase cannot copy
 * blindly: a production heap snapshot here found 987 undici `_Response` objects and 1,720
 * queued chunks holding 15.4 MB of buffers nobody intended to read, which is why
 * `drainResponseBody` exists at all. Holding one extra body per URL per render, across every
 * concurrent render, is the same shape of cost.
 *
 * So this shares the request only while it is **in flight**. Callers that arrive during the
 * window get the same answer; the slot is released the moment it resolves, and the captured
 * bytes become garbage as soon as the last joiner has taken its copy. Nothing is retained for
 * the render.
 *
 * What that gives up is a sequential repeat — a second read after the first has resolved
 * still issues its own request, where Next's memoization would have served it. That case is
 * already covered by the Data Cache on a hit, and it is not the shape that breaks: duplicates
 * here come from `Promise.all` and from metadata rendering beside its page, which fire
 * together.
 *
 * ## What a joiner gets
 *
 * The same status, headers and bytes, in its own `Response` object — a body reads once, so
 * one is reproduced per caller. It does **not** get its own attempt telemetry or its own
 * retry schedule; `inFlightKey` keeps those callers out rather than misreporting them.
 */
export const fetchServerSide = async (
    url: string,
    options?: RequestInit,
    behavior: FetchServerSideBehavior = {},
): Promise<Response | null> => {
    // Normalized before the key is taken, so the added header is part of the identity rather
    // than an invisible difference between two otherwise-identical calls.
    if (url.includes('.gz')) {
        options = {
            ...options,
            headers: { ...options?.headers, 'Accept-Encoding': 'gzip' },
        };
    }

    const key = inFlightKey(url, options, behavior);
    if (key === null) return executeFetchServerSide(url, options, behavior);

    const slot = getInFlightSlot(key);
    if (slot.promise) {
        // Registered before awaiting, and synchronously, which is what makes the leader's
        // check below sound: a joiner that saw an open slot has already set this.
        slot.joined = true;
        return takeResult(await slot.promise);
    }

    /**
     * Give up the slot, but only while it is still this request's.
     *
     * Both callers of this run after the request settles, and by then the slot may belong to
     * somebody else: the callback below clears it for an unjoined request, and in the
     * microtask window before the `await` resumes, another caller can find it empty and
     * install itself as the next leader. Clearing unconditionally would erase *that* entry
     * while its request is still in flight, so the caller after it would go to the origin
     * instead of joining — re-creating the duplicate burst this whole mechanism exists to
     * stop, in the one case hardest to notice.
     *
     * Reading `inFlight` here is safe despite it being declared below: neither caller can run
     * before the promise it is attached to has settled.
     */
    function releaseSlot(): void {
        if (slot.promise === inFlight) slot.promise = null;
    }

    slot.joined = false;
    const inFlight = executeFetchServerSide(url, options, behavior).then(async (response): Promise<InFlightResult> => {
        if (!slot.joined) {
            // Nobody joined, so nothing has to be reproduced. Close the slot *synchronously*,
            // before the first `await` below, so a late arrival cannot attach to a promise
            // whose body this caller is about to consume — it starts its own request instead.
            releaseSlot();
            return { shared: false, response };
        }
        return { shared: true, captured: await captureResponse(response) };
    });
    slot.promise = inFlight;
    try {
        return takeResult(await inFlight);
    } finally {
        // Released as soon as the request settles, which is what keeps this a coalescer and
        // not a render-lifetime cache. Joiners already hold `inFlight` and still resolve; the
        // captured bytes are unreachable once the last of them has taken its copy.
        releaseSlot();
    }
};

const takeResult = (result: InFlightResult): Response | null =>
    result.shared ? replayResponse(result.captured) : result.response;

const executeFetchServerSide = async (
    url: string,
    options: RequestInit | undefined,
    behavior: FetchServerSideBehavior,
): Promise<Response | null> => {
    const { expectedFailure = false, failureLogLevel = 'error', retryTransientErrors = isBuildPhase } = behavior;
    try {
        if (dispatcherReady) {
            await dispatcherReady;
        }
        // A caller's own signal, if it passed one. It bounds the *whole* call rather than one
        // attempt — the retry loop below reuses it, because there is no way to derive a fresh
        // deadline from a signal — so once it fires, every remaining attempt aborts the
        // instant it is made. `callerDeadlineSpent` is what stops the loop sleeping through a
        // schedule it can no longer use.
        const callerSignal = options?.signal ?? undefined;
        const send = () =>
            fetchWithAwsWaf(url, {
                ...options,
                signal: callerSignal ?? AbortSignal.timeout(requestTimeoutMs()),
            });

        // An expected failure wants the catch below: a warning and a null, not a throw, and
        // no backoff — a discardable read has nothing to salvage by burning the schedule.
        // It has to `await` to get there. The plain paths deliberately do not: an async
        // function settles a returned promise *outside* the try, so `return send()` lets a
        // rejection reach the caller rather than this catch, which is the behaviour the
        // other callers of this function are written against.
        //
        // Checked ahead of the retry gate rather than folded into it, so a discardable
        // read opts out of the backoff even during `next build`, and even if its caller
        // also asked for transient retries.
        if (expectedFailure) {
            try {
                const response = await send();
                notifyAttempt(behavior, {
                    attempt: 1,
                    maxAttempts: 1,
                    outcome: 'response',
                    status: response.status,
                    willRetry: false,
                });
                return response;
            } catch (error) {
                notifyAttempt(behavior, {
                    attempt: 1,
                    maxAttempts: 1,
                    outcome: 'network_error',
                    willRetry: false,
                });
                throw error;
            }
        }

        if (!retryTransientErrors) {
            const responsePromise = logEscapingRejection(url, send());
            if (!behavior.onAttempt) return responsePromise;
            return responsePromise.then(
                (response) => {
                    notifyAttempt(behavior, {
                        attempt: 1,
                        maxAttempts: 1,
                        outcome: 'response',
                        status: response.status,
                        willRetry: false,
                    });
                    return response;
                },
                (error) => {
                    notifyAttempt(behavior, {
                        attempt: 1,
                        maxAttempts: 1,
                        outcome: 'network_error',
                        willRetry: false,
                    });
                    throw error;
                },
            );
        }

        // `next build` retries broadly because it fans out across workers and a transient
        // failure can fail the deploy. Runtime retries are explicit and shorter: cache-safe
        // ISR fills need protection on a MISS, while ordinary request-scoped reads retain the
        // single-attempt behavior above. The schedule itself is chosen per attempt — see
        // `RUNTIME_RATE_LIMIT_RETRY_DELAYS_MS` — so `maxAttempts` here is the ceiling the
        // telemetry reports, reached only by a request whose every failure earns the longest
        // applicable schedule.
        const maxAttempts =
            (isBuildPhase
                ? BUILD_RETRY_DELAYS_MS.length
                : Math.max(RUNTIME_RETRY_DELAYS_MS.length, RUNTIME_RATE_LIMIT_RETRY_DELAYS_MS.length)) + 1;
        // Only a build gets a wall clock; a runtime fill's few short retries need no bounding
        // beyond their own schedules, and a request-scoped read has a client waiting on it.
        const retryDeadline = isBuildPhase ? Date.now() + BUILD_RETRY_BUDGET_MS : undefined;
        for (let attempt = 0; ; attempt++) {
            let response: Response | undefined;
            let failure: unknown;
            // Tracked separately from `failure` being set, because a thrown value may itself
            // be falsy and this decides whether the attempt is rethrown or returned.
            let threw = false;
            let retryableFailure: boolean;
            try {
                response = await send();
                retryableFailure = isRetryableStatus(response.status);
            } catch (error) {
                failure = error;
                threw = true;
                retryableFailure = isRetryableFetchError(error);
            }

            // What this attempt got decides which schedule governs it: a build keeps its one
            // long schedule, and at runtime a 429 — fast to receive, cheap to repeat, gone
            // when the limiter window turns — earns more patience than the failures that each
            // cost a deadline. Chosen inside the loop because the status can differ from one
            // attempt to the next, and each failure kind should exhaust its *own* schedule.
            const rateLimited = !threw && response?.status === 429;
            const retryDelays = isBuildPhase
                ? BUILD_RETRY_DELAYS_MS
                : rateLimited
                  ? RUNTIME_RATE_LIMIT_RETRY_DELAYS_MS
                  : RUNTIME_RETRY_DELAYS_MS;
            const maxRetryDelayMs = isBuildPhase
                ? MAX_BUILD_RETRY_DELAY_MS
                : rateLimited
                  ? MAX_RUNTIME_RATE_LIMIT_RETRY_DELAY_MS
                  : MAX_RUNTIME_RETRY_DELAY_MS;
            // The delay has to be known before `willRetry` is, because the budget is checked
            // against the wait this retry would take rather than against the time already
            // spent: a retry that would sleep past the deadline cannot help the page it is
            // holding up, so the loop declines to start it instead of overshooting by a sleep
            // it has already committed to.
            const attemptsLeft = attempt < retryDelays.length;
            const delay = attemptsLeft ? retryDelayMs(response, retryDelays[attempt], maxRetryDelayMs) : 0;
            const withinBudget = retryDeadline === undefined || Date.now() + delay <= retryDeadline;
            // A caller whose own deadline has passed cannot be helped by another attempt: the
            // shared signal makes every one of them abort on issue. Retrying anyway would
            // sleep out the rest of the schedule — up to the full `BUILD_RETRY_BUDGET_MS` —
            // to issue requests that are dead before they leave, and a caller that retries on
            // its own deadline (`readNflPowerRankingsWithRetry`) would then do it all again.
            const callerDeadlineSpent = callerSignal?.aborted === true;
            const willRetry = retryableFailure && attemptsLeft && withinBudget && !callerDeadlineSpent;

            notifyAttempt(
                behavior,
                threw
                    ? { attempt: attempt + 1, maxAttempts, outcome: 'network_error', willRetry }
                    : { attempt: attempt + 1, maxAttempts, outcome: 'response', status: response?.status, willRetry },
            );

            if (!willRetry) {
                if (threw) throw failure;
                // `retryableFailure` without `willRetry` means the budget or the schedule is
                // spent: hand the final non-OK response back as-is, the way an exhausted
                // retry loop always has, so the caller can read its status rather than
                // seeing a throw it cannot classify.
                return response as Response;
            }

            const reason = threw ? 'a network error' : `HTTP ${response?.status}`;
            const message = `${isBuildPhase ? 'Build prerender' : 'Runtime cache fill'} got ${reason} from ${redactUrlForLog(url)} — retrying in ${delay}ms (attempt ${attempt + 1}/${retryDelays.length})`;
            // A production-backed build's retry is worth an alert: it is the only advance
            // warning that the deploy pipeline is being throttled, before a request that runs
            // out of budget fails the build outright.
            //
            // A demo-backed one is not. Demo is shared and the load is usually someone else's,
            // and the schedule above now allows seven retries per request where it used to
            // allow three — so error-level on each would multiply the noise from every preview
            // of every PR and every "Build E2E App" run. Those builds still *fail* loudly if
            // the budget runs out; this only declines to page anyone while it is being spent.
            //
            // This is the only thing keyed on the backend, and it decides who gets paged, not
            // what the request does.
            if (isBuildPhase && !isDemoBackedBuild()) {
                logProviderFactory.logError(message);
            } else {
                logProviderFactory.logWarning(message);
            }
            // A returned response is superseded by the retry and nobody will ever read it.
            // Release its buffered body rather than leaving undici's chunk queue reachable.
            if (response) drainResponseBody(response);
            await waitForRetry(delay, callerSignal);
        }
    } catch (e) {
        const error = `Error fetching server-side data from ${redactUrlForLog(url)}: ${e}`;
        if (expectedFailure || failureLogLevel === 'warning') {
            logProviderFactory.logWarning(error);
        } else {
            logProviderFactory.logError(error);
        }
        return null;
    }
};
